import { createSlice } from "@reduxjs/toolkit";
import {
  getAllCutiiPicking,
  getCutiePicking,
  editCutiePicking,
  createCutiePicking,
  getHistoryCutiePicking,
  getPickingBoxTypes,
  getPickingBoxTmsServices,
  getDepositStocks,
  getDuplicateProducts,
  getProductHistory,
  getProductsDestroyedRaports,
  getProductsThatExpire,
  getQuarantinedProductsToDestroy,
  getQuarantinedProductsToReturn,
  getReceivedProducts,
  getSelectProductWms,
  postGetProducts,
} from "../api/inventory.api";
import {
  DuplicateProductsDataInterface,
  InventoryDepositStocksDataInterface,
  InventoryProductsDataInterface,
  InventoryProductsRaportsDestroyedDataInterface,
  InventoryProductsThatExpireDataInterface,
  InventoryQuarantinedProductsToDestroyDataInterface,
  ProductHistoryDataInterface,
  ReceivedProductsInterface,
  ReturningProductsInterface,
  SelectProductWmsInterface,
} from "../interfaces/inventory.interface";

// Transport state only; domain interfaces follow the observed API responses.
export type PickingBoxResponseState = {
  data: unknown;
  status: "idle" | "loading" | "success" | "error";
  errorMessage: string;
  requestId?: string;
};

const emptyPickingBoxResponse: PickingBoxResponseState = {
  data: null,
  status: "idle",
  errorMessage: "",
};

const initialPickingBoxesState = {
  types: { ...emptyPickingBoxResponse },
  tmsServices: { ...emptyPickingBoxResponse },
  save: { ...emptyPickingBoxResponse },
  list: { ...emptyPickingBoxResponse },
  edit: { ...emptyPickingBoxResponse },
  history: { ...emptyPickingBoxResponse },
};

const initialInventoryState: {
  pickingBoxes: typeof initialPickingBoxesState;
  receivedProducts: ReceivedProductsInterface;
  depositStocks: InventoryDepositStocksDataInterface;
  products: InventoryProductsDataInterface;
  selectProductWms: SelectProductWmsInterface;
  productsThatExpire: InventoryProductsThatExpireDataInterface;
  quarantinedProductsToDestroy: InventoryQuarantinedProductsToDestroyDataInterface;
  productsDestroyedRaports: InventoryProductsRaportsDestroyedDataInterface;
  productHistory: ProductHistoryDataInterface;
  duplicateProducts: DuplicateProductsDataInterface;
  quarantinedProductsToReturn: ReturningProductsInterface;
} = {
  pickingBoxes: initialPickingBoxesState,
  receivedProducts: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  depositStocks: {
    data: {
      nrPagini: 1,
      nrTotalStoc: 1,
      stocuri: [],
    },
    isLoadingDepositStocks: false,
    isErrorDepositStocks: false,
    errorMessageDepositStocks: "",
  },
  products: {
    data: {
      nrTotal: 1,
      produseWms: [],
    },
    isLoadingProducts: false,
    isErrorProducts: false,
    errorMessageProducts: "",
  },
  selectProductWms: {
    data: null,
    isLoadingSelectProductWms: false,
    isErrorSelecProductWms: false,
    errorMessageSelectProductWms: "",
  },
  productsThatExpire: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  quarantinedProductsToDestroy: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  productsDestroyedRaports: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  productHistory: {
    data: {
      total: 1,
      current_page: 1,
      furnizor: "",
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  duplicateProducts: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  quarantinedProductsToReturn: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
};

const inventoryReducer = createSlice({
  name: "inventory",
  initialState: initialInventoryState,
  reducers: {
    clearPickingBoxes: (state) => {
      state.pickingBoxes = initialPickingBoxesState;
    },
    clearDepositStocks: (state) => {
      return {
        ...state,
        depositStocks: {
          ...initialInventoryState.depositStocks,
        },
      };
    },
    clearSelectProductWms: (state) => {
      return {
        ...state,
        selectProductWms: {
          ...initialInventoryState.selectProductWms,
        },
      };
    },
    clearQuarantinedProducts: (state) => {
      return {
        ...state,
        quarantinedProductsToDestroy: {
          ...initialInventoryState.quarantinedProductsToDestroy,
        },
      };
    },
    clearQuarantinedProductsToReturn: (state) => {
      return {
        ...state,
        quarantinedProductsToReturn: {
          ...initialInventoryState.quarantinedProductsToReturn,
        },
      };
    },

    setProductsBlockedStatus: (state, action) => {
      const newProducts = state.products.data.produseWms.map((product) => {
        if (product.idProdus === action.payload) {
          return {
            ...product,
            blocat: product.blocat ? 0 : 1,
          };
        }
        return product;
      });

      return {
        ...state,
        products: {
          ...state.products,
          data: {
            ...state.products.data,
            produseWms: newProducts,
          },
        },
      };
    },
  },
  extraReducers: (builder) => {
    const pickingBoxRequests = [
      { thunk: getPickingBoxTypes, key: "types" as const },
      { thunk: getPickingBoxTmsServices, key: "tmsServices" as const },
      { thunk: editCutiePicking, key: "save" as const },
      { thunk: createCutiePicking, key: "save" as const },
      { thunk: getAllCutiiPicking, key: "list" as const },
      { thunk: getCutiePicking, key: "edit" as const },
      { thunk: getHistoryCutiePicking, key: "history" as const },
    ];
    pickingBoxRequests.forEach(({ thunk, key }) => {
      builder.addCase(thunk.pending, (state, action) => {
        state.pickingBoxes[key] = {
          ...emptyPickingBoxResponse,
          status: "loading",
          requestId: action.meta.requestId,
        };
      });
      builder.addCase(thunk.fulfilled, (state, action) => {
        if (state.pickingBoxes[key].requestId !== action.meta.requestId) return;
        state.pickingBoxes[key] = {
          data: action.payload,
          status: "success",
          errorMessage: "",
        };
      });
      builder.addCase(thunk.rejected, (state, action) => {
        if (state.pickingBoxes[key].requestId !== action.meta.requestId) return;
        const error = action.payload as { message?: string } | undefined;
        state.pickingBoxes[key] = {
          ...emptyPickingBoxResponse,
          status: "error",
          errorMessage:
            error?.message || action.error.message || "Cererea a esuat.",
        };
      });
    });
    // start  received products
    builder.addCase(getReceivedProducts.pending, (state) => {
      return {
        ...state,
        receivedProducts: {
          ...initialInventoryState.receivedProducts,
          isLoading: true,
        },
      };
    });
    builder.addCase(getReceivedProducts.rejected, (state, action: any) => {
      return {
        ...state,
        receivedProducts: {
          ...state.receivedProducts,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getReceivedProducts.fulfilled, (state, action) => {
      return {
        ...state,
        receivedProducts: {
          ...state.receivedProducts,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // end received products
    // start deposit stocks
    builder.addCase(getDepositStocks.pending, (state) => {
      return {
        ...state,
        depositStocks: {
          ...initialInventoryState.depositStocks,
          isLoadingDepositStocks: true,
        },
      };
    });
    builder.addCase(getDepositStocks.rejected, (state, action: any) => {
      return {
        ...state,
        depositStocks: {
          ...state.depositStocks,
          isLoadingDepositStocks: false,
          errorMessageDepositStocks: action.payload.message,
          isErrorDepositStocks: true,
        },
      };
    });
    builder.addCase(getDepositStocks.fulfilled, (state, action) => {
      return {
        ...state,
        depositStocks: {
          ...state.depositStocks,
          isLoadingDepositStocks: false,
          data: action.payload,
        },
      };
    });
    // end deposit stocks
    // start get products
    builder.addCase(postGetProducts.pending, (state) => {
      return {
        ...state,
        products: {
          ...initialInventoryState.products,
          isLoadingProducts: true,
        },
      };
    });
    builder.addCase(postGetProducts.rejected, (state, action: any) => {
      return {
        ...state,
        products: {
          ...state.products,
          isLoadingProducts: false,
          errorMessageProducts: action.payload.message,
          isErrorProducts: true,
        },
      };
    });
    builder.addCase(postGetProducts.fulfilled, (state, action) => {
      return {
        ...state,
        products: {
          ...state.products,
          isLoadingProducts: false,
          data: action.payload,
        },
      };
    });
    // end get products
    // start select product wms
    builder.addCase(getSelectProductWms.pending, (state) => {
      return {
        ...state,
        selectProductWms: {
          ...initialInventoryState.selectProductWms,
          isLoadingSelectProductWms: true,
        },
      };
    });
    builder.addCase(getSelectProductWms.rejected, (state, action: any) => {
      return {
        ...state,
        selectProductWms: {
          ...state.selectProductWms,
          isLoadingSelectProductWms: false,
          errorMessageSelectProductWms: action.payload.message,
          isErrorSelecProductWms: true,
        },
      };
    });
    builder.addCase(getSelectProductWms.fulfilled, (state, action) => {
      return {
        ...state,
        selectProductWms: {
          ...state.selectProductWms,
          isLoadingSelectProductWms: false,
          data: action.payload,
        },
      };
    });
    // end select product wms

    // start get products that expire
    builder.addCase(getProductsThatExpire.pending, (state) => {
      return {
        ...state,
        productsThatExpire: {
          ...initialInventoryState.productsThatExpire,
          isLoading: true,
        },
      };
    });
    builder.addCase(getProductsThatExpire.rejected, (state, action: any) => {
      return {
        ...state,
        productsThatExpire: {
          ...state.productsThatExpire,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getProductsThatExpire.fulfilled, (state, action) => {
      return {
        ...state,
        productsThatExpire: {
          ...state.productsThatExpire,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // end get products that expire

    // start get quarantined products to destroy
    builder.addCase(getQuarantinedProductsToDestroy.pending, (state) => {
      return {
        ...state,
        quarantinedProductsToDestroy: {
          ...initialInventoryState.quarantinedProductsToDestroy,
          isLoading: true,
        },
      };
    });
    builder.addCase(
      getQuarantinedProductsToDestroy.rejected,
      (state, action: any) => {
        return {
          ...state,
          quarantinedProductsToDestroy: {
            ...state.quarantinedProductsToDestroy,
            isLoading: false,
            errorMessage: action.payload.message,
            isError: true,
          },
        };
      },
    );
    builder.addCase(
      getQuarantinedProductsToDestroy.fulfilled,
      (state, action) => {
        return {
          ...state,
          quarantinedProductsToDestroy: {
            ...state.quarantinedProductsToDestroy,
            isLoading: false,
            data: action.payload,
          },
        };
      },
    );
    // end get quarantined products to destroy

    // start get products destroyed raports
    builder.addCase(getProductsDestroyedRaports.pending, (state) => {
      return {
        ...state,
        productsDestroyedRaports: {
          ...initialInventoryState.productsDestroyedRaports,
          isLoading: true,
        },
      };
    });
    builder.addCase(
      getProductsDestroyedRaports.rejected,
      (state, action: any) => {
        return {
          ...state,
          productsDestroyedRaports: {
            ...state.productsDestroyedRaports,
            isLoading: false,
            errorMessage: action.payload.message,
            isError: true,
          },
        };
      },
    );
    builder.addCase(getProductsDestroyedRaports.fulfilled, (state, action) => {
      return {
        ...state,
        productsDestroyedRaports: {
          ...state.productsDestroyedRaports,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // end get products destroyed raports

    // start product history
    builder.addCase(getProductHistory.pending, (state) => {
      return {
        ...state,
        productHistory: {
          ...initialInventoryState.productHistory,
          isLoading: true,
        },
      };
    });
    builder.addCase(getProductHistory.rejected, (state, action: any) => {
      return {
        ...state,
        productHistory: {
          ...state.productHistory,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getProductHistory.fulfilled, (state, action) => {
      return {
        ...state,
        productHistory: {
          ...state.productHistory,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // end product history

    // start duplicate products
    builder.addCase(getDuplicateProducts.pending, (state) => {
      return {
        ...state,
        duplicateProducts: {
          ...initialInventoryState.duplicateProducts,
          isLoading: true,
        },
      };
    });
    builder.addCase(getDuplicateProducts.rejected, (state, action: any) => {
      return {
        ...state,
        duplicateProducts: {
          ...state.duplicateProducts,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getDuplicateProducts.fulfilled, (state, action) => {
      return {
        ...state,
        duplicateProducts: {
          ...state.duplicateProducts,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // end duplicate products

    // start get quarantined products to return
    builder.addCase(getQuarantinedProductsToReturn.pending, (state) => {
      return {
        ...state,
        quarantinedProductsToReturn: {
          ...initialInventoryState.quarantinedProductsToReturn,
          isLoading: true,
        },
      };
    });
    builder.addCase(
      getQuarantinedProductsToReturn.rejected,
      (state, action: any) => {
        return {
          ...state,
          quarantinedProductsToReturn: {
            ...state.quarantinedProductsToReturn,
            isLoading: false,
            errorMessage: action.payload.message,
            isError: true,
          },
        };
      },
    );
    builder.addCase(
      getQuarantinedProductsToReturn.fulfilled,
      (state, action) => {
        return {
          ...state,
          quarantinedProductsToReturn: {
            ...state.quarantinedProductsToReturn,
            isLoading: false,
            data: action.payload,
          },
        };
      },
    );
    // end get quarantined products to return
  },
});

export const {
  clearPickingBoxes,
  clearDepositStocks,
  clearSelectProductWms,
  clearQuarantinedProducts,
  clearQuarantinedProductsToReturn,
  setProductsBlockedStatus,
} = inventoryReducer.actions;

export default inventoryReducer.reducer;
